import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';

export interface ConfigResponse {
  apiUrl: string;
  apiKey: string;
}

interface CachedConfig {
  apiUrl: string;
  apiKey: string;
}

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  // Embedded decryption key (must match backend Encryption:Key)
  private readonly DECRYPTION_KEY = 'Qw8zLk2pVb6nRj4sTg1xYc7uZp3eHs5d';
  private cache: Map<string, CachedConfig> = new Map();
  private loadingPromises: Map<string, Promise<CachedConfig>> = new Map();

  constructor(
    private http: HttpClient,
    @Inject(OKTA_AUTH) private auth: OktaAuth,
    @Inject('CONFIG_SETTINGS') private configSettings: any
  ) { }

  /**
   * Gets configuration for the specified key
   * @param key Configuration key: 'chat', 'chatagents', 'prompt-library', or 'voicestorm'
   * @returns Promise with decrypted apiUrl and apiKey
   */
  async getConfig(key: string): Promise<ConfigResponse> {
    // Check cache first
    const cached = this.cache.get(key);
    if (cached) {
      return cached;
    }

    // Check if already loading
    const loadingPromise = this.loadingPromises.get(key);
    if (loadingPromise) {
      return loadingPromise;
    }

    // Start loading
    const promise = this.loadConfig(key);
    this.loadingPromises.set(key, promise);

    try {
      const config = await promise;
      this.cache.set(key, config);
      return config;
    } finally {
      this.loadingPromises.delete(key);
    }
  }

  /**
   * Loads configuration from backend API
   */
  private async loadConfig(key: string): Promise<CachedConfig> {
    try {
      const token = this.auth.getAccessToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Get base URL from general config
      const generalConfig = this.configSettings['general'];
      const baseUrl = generalConfig['adminApiUrl'];
      const apiKey = generalConfig['apiKey'];

      const url = `${baseUrl}Configuration/${key}`;
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Ocp-Apim-Subscription-Key': apiKey
      });

      const response = await this.http.get<{ apiUrl: string; apiKey: string }>(url, { headers }).toPromise();

      if (!response) {
        throw new Error('No response from configuration endpoint');
      }

      // Decrypt the values
      const decryptedConfig: CachedConfig = {
        apiUrl: await this.decrypt(response.apiUrl),
        apiKey: await this.decrypt(response.apiKey)
      };

      return decryptedConfig;
    } catch (error) {
      console.error(`Error loading config for key '${key}':`, error);
      // Fallback to appconfig.json if available
      return this.getFallbackConfig(key);
    }
  }

  /**
   * Fallback to appconfig.json values (for backward compatibility)
   */
  private getFallbackConfig(key: string): CachedConfig {
    const config = this.configSettings[key];
    if (config && config.apiUrl && config.apiKey) {
      return {
        apiUrl: config.apiUrl,
        apiKey: config.apiKey
      };
    }
    throw new Error(`Configuration not found for key: ${key}`);
  }

  /**
   * Decrypts an encrypted string using AES-256-CBC
   * @param encryptedText Base64 encoded encrypted string
   * @returns Decrypted plain text
   */
  private async decrypt(encryptedText: string): Promise<string> {
    try {
      // Convert key to ArrayBuffer
      const keyData = new TextEncoder().encode(this.DECRYPTION_KEY);
      const keyBuffer = await crypto.subtle.importKey(
        'raw',
        keyData,
        { name: 'AES-CBC' },
        false,
        ['decrypt']
      );

      // Decode base64
      const encryptedData = this.base64ToUint8Array(encryptedText);

      // Extract IV (first 16 bytes) and ciphertext
      const iv = encryptedData.slice(0, 16);
      const ciphertext = encryptedData.slice(16);

      // Decrypt
      const decrypted = await crypto.subtle.decrypt(
        {
          name: 'AES-CBC',
          iv: iv
        },
        keyBuffer,
        ciphertext
      );

      return new TextDecoder().decode(decrypted);
    } catch (error) {
      console.error('Decryption error:', error);
      throw new Error('Failed to decrypt configuration');
    }
  }

  /**
   * Converts base64 string to Uint8Array
   */
  private base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }
}

